import { useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import {
  LockedAutocomplete,
  type AutocompleteOption,
} from "@/components/LockedAutocomplete";
import { api } from "@/lib/apiClient";
import {
  articleSource,
  pitchCategorySource,
} from "@/lib/autocompleteSources";
import { GUEST_TYPES } from "@/lib/guestType";
import { CHARGE_RULE_LABELS } from "@/lib/chargeRuleLabels";
import type { Article, GuestTypeValue, PitchCategory } from "@/types/camping";

const RULE_LABELS = CHARGE_RULE_LABELS;

const EMPTY_FORM = {
  rule_type: "pitch_category_night",
  pitch_category_id: "",
  guest_type: "" as "" | GuestTypeValue,
  article_id: "",
};

export interface ChargeRuleCreateInitialValues {
  rule_type?: string;
  pitch_category_id?: string | null;
  guest_type?: GuestTypeValue | null;
}

export interface CreateChargeRuleModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  articles: Article[];
  categories: PitchCategory[];
  initialValues?: ChargeRuleCreateInitialValues | null;
}

export function CreateChargeRuleModal({
  open,
  onClose,
  onCreated,
  articles,
  categories,
  initialValues,
}: CreateChargeRuleModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [articleOption, setArticleOption] = useState<AutocompleteOption<Article> | null>(null);
  const [categoryOption, setCategoryOption] = useState<AutocompleteOption<PitchCategory> | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadArticleOptions = useCallback(
    (query: string) => articleSource(articles, query),
    [articles],
  );
  const loadCategoryOptions = useCallback(
    (query: string) => pitchCategorySource(categories, query),
    [categories],
  );

  useEffect(() => {
    if (!open) return;
    const nextForm = {
      rule_type: initialValues?.rule_type ?? EMPTY_FORM.rule_type,
      pitch_category_id: initialValues?.pitch_category_id ?? "",
      guest_type: (initialValues?.guest_type ?? "") as "" | GuestTypeValue,
      article_id: "",
    };
    setForm(nextForm);
    setArticleOption(null);
    setCategoryOption(
      nextForm.pitch_category_id
        ? (() => {
            const category = categories.find((item) => item.id === nextForm.pitch_category_id);
            return category
              ? {
                  key: category.id,
                  label: category.name,
                  value: category,
                }
              : null;
          })()
        : null,
    );
    setError(null);
    setSubmitting(false);
  }, [categories, initialValues, open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/charge-rules", {
        rule_type: form.rule_type,
        pitch_category_id:
          form.rule_type === "pitch_category_night" ? form.pitch_category_id || null : null,
        guest_type: form.guest_type || null,
        article_id: form.article_id,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neue Zuordnung"
      description="Artikel einer Buchungsregel zuordnen"
      size="lg"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="flex flex-col gap-1 text-sm">
          Regel
          <select
            className="eh-select-input"
            value={form.rule_type}
            onChange={(e) => setForm((f) => ({ ...f, rule_type: e.target.value }))}
          >
            {Object.entries(RULE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Gästetyp (optional)
          <select
            className="eh-select-input"
            value={form.guest_type}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                guest_type: e.target.value as "" | GuestTypeValue,
              }))
            }
          >
            <option value="">Alle Gästetypen</option>
            {GUEST_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <span className="text-label-secondary">
            Nur für diesen Gästetyp; leer = gilt für alle Typen.
          </span>
        </label>
        {form.rule_type === "pitch_category_night" ? (
          <LockedAutocomplete
            label="Platzkategorie"
            required
            placeholder="Kategorie suchen…"
            value={categoryOption}
            loadOptions={loadCategoryOptions}
            onChange={(option) => {
              setCategoryOption(option);
              setForm((f) => ({ ...f, pitch_category_id: option?.value.id ?? "" }));
            }}
          />
        ) : null}
        <LockedAutocomplete
          label="Artikel"
          required
          placeholder="Artikelnummer oder Bezeichnung suchen…"
          value={articleOption}
          loadOptions={loadArticleOptions}
          onChange={(option) => {
            setArticleOption(option);
            setForm((f) => ({ ...f, article_id: option?.value.id ?? "" }));
          }}
        />
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting}>
            Zuordnung speichern
          </Button>
        </div>
      </form>
    </Modal>
  );
}